<template>

    <section
        id="testimonials"
        class="testimonials"
    >

        <div class="container">

            <div class="testimonials__header">

                <span class="testimonials__eyebrow">
                    {{ 'home_testimonials' | staticContentAlt }}
                </span>

                <h2>
                    {{ 'home_testimonials' | staticTitle }}
                </h2>

                <p>
                    {{ 'home_testimonials' | staticContent }}
                </p>

            </div>

            <div
                v-if="testimonials.length"
                class="testimonials__grid"
                :class="{
                    'testimonials__grid--single':
                        testimonials.length === 1,

                    'testimonials__grid--double':
                        testimonials.length === 2
                }"
            >

                <article
                    v-for="(item, index) in testimonials"
                    :key="item.id"
                    class="testimonial-card"
                >

                    <div class="testimonial-card__top">

                        <span class="testimonial-card__number">
                            0{{ index + 1 }}
                        </span>

                        <span class="testimonial-card__quote">
                            “
                        </span>

                    </div>

                    <blockquote>
                        {{ item.content }}
                    </blockquote>

                    <div class="testimonial-card__author">

                        <div
                            v-if="item.image"
                            class="testimonial-card__image"
                        >

                            <img
                                :src="item.image"
                                :alt="item.title"
                            >

                        </div>

                        <div class="testimonial-card__author-info">

                            <strong>
                                {{ item.title }}
                            </strong>

                            <span v-if="item.contentAlt">
                                {{ item.contentAlt }}
                            </span>

                        </div>

                    </div>

                </article>

            </div>

        </div>

    </section>

</template>

<script>
import { imageIdToURL } from '@/utils';

export default {
    name: 'HomeTestimonials',

    computed: {
        testimonials() {
            const items =
                this.$options.filters.portfolioFetchBy(
                    'testimonial'
                );

            if (
                !Array.isArray(items) ||
                !items.length
            ) {
                return [];
            }

            return items.map(item => ({
                id:
                    item.id,

                title:
                    item.title || '',

                content:
                    item.content || '',

                contentAlt:
                    item.content_alt || '',

                image:
                    item.imagenes_id
                        ? imageIdToURL(
                            item.imagenes_id
                        )
                        : ''
            }));
        }
    }
};
</script>

<style lang="scss" scoped>
.testimonials {
    position: relative;

    padding:
        100px 20px
        115px;

    background:
        radial-gradient(
            circle at 85% 15%,
            rgba(233, 81, 29, .045),
            transparent 28%
        ),
        var(--color-2);

    border-top:
        1px solid
        var(--border-1);

    overflow: hidden;
}

.testimonials::before {
    content: "";

    position: absolute;

    top: -250px;
    left: -230px;

    width: 520px;
    height: 520px;

    border:
        1px solid
        rgba(255, 255, 255, .035);

    border-radius: 50%;

    pointer-events: none;
}

.container {
    position: relative;

    width: 100%;

    max-width:
        var(--container);

    margin:
        0 auto;

    z-index: 2;
}

.testimonials__header {
    max-width:
        760px;

    margin:
        0 auto
        60px;

    text-align:
        center;

    h2 {
        margin:
            0 0
            18px;

        color:
            var(--text-1);

        font-size:
            clamp(
                38px,
                7vw,
                64px
            );

        font-weight:
            700;

        line-height:
            .95;

        letter-spacing:
            -2px;

        text-transform:
            uppercase;
    }

    p {
        max-width:
            620px;

        margin:
            0 auto;

        color:
            var(--text-2);

        font-size:
            14px;

        line-height:
            1.8;
    }
}

.testimonials__eyebrow {
    display:
        inline-flex;

    align-items:
        center;

    gap:
        12px;

    margin-bottom:
        16px;

    color:
        var(--text-3);

    font-family:
        var(--font-tech);

    font-size:
        10px;

    font-weight:
        600;

    letter-spacing:
        2.5px;

    text-transform:
        uppercase;

    &::before,
    &::after {
        content: "";

        width:
            24px;

        height:
            1px;

        background:
            var(--color-3);
    }
}

.testimonials__grid {
    display:
        grid;

    grid-template-columns:
        1fr;

    gap:
        20px;

    max-width:
        1180px;

    margin:
        0 auto;
}

.testimonial-card {
    position:
        relative;

    display:
        flex;

    flex-direction:
        column;

    min-height:
        320px;

    padding:
        28px 24px;

    border:
        1px solid
        var(--border-1);

    background:
        linear-gradient(
            145deg,
            rgba(255, 255, 255, .018),
            rgba(255, 255, 255, .005)
        );

    transition:
        transform .35s ease,
        border-color .35s ease,
        background .35s ease;

    &:hover {
        transform:
            translateY(-5px);

        border-color:
            rgba(233, 81, 29, .32);

        background:
            rgba(255, 255, 255, .025);
    }
}

.testimonial-card__top {
    display:
        flex;

    align-items:
        flex-start;

    justify-content:
        space-between;

    margin-bottom:
        28px;
}

.testimonial-card__number {
    color:
        var(--text-3);

    font-family:
        var(--font-tech);

    font-size:
        9px;

    letter-spacing:
        1.4px;
}

.testimonial-card__quote {
    color:
        var(--color-3);

    font-size:
        52px;

    line-height:
        .7;

    font-weight:
        300;

    opacity:
        .8;
}

.testimonial-card blockquote {
    margin:
        0 0
        34px;

    color:
        var(--text-1);

    font-size:
        clamp(
            17px,
            2vw,
            20px
        );

    font-weight:
        400;

    line-height:
        1.7;
}

.testimonial-card__author {
    display:
        flex;

    align-items:
        center;

    gap:
        14px;

    margin-top:
        auto;

    padding-top:
        22px;

    border-top:
        1px solid
        var(--border-1);
}

.testimonial-card__image {
    flex-shrink:
        0;

    width:
        48px;

    height:
        48px;

    overflow:
        hidden;

    border:
        1px solid
        var(--border-1);

    border-radius:
        50%;

    img {
        display:
            block;

        width:
            100%;

        height:
            100%;

        object-fit:
            cover;
    }
}

.testimonial-card__author-info {
    min-width: 0;

    strong {
        display:
            block;

        margin-bottom:
            5px;

        color:
            var(--text-1);

        font-size:
            14px;

        font-weight:
            600;
    }

    span {
        display:
            block;

        color:
            var(--text-3);

        font-family:
            var(--font-tech);

        font-size:
            9px;

        letter-spacing:
            1.3px;

        text-transform:
            uppercase;
    }
}

@media (min-width: 768px) {
    .testimonials {
        padding:
            115px 40px
            130px;
    }

    .testimonials__grid {
        grid-template-columns:
            repeat(2, 1fr);
    }

    .testimonials__grid--single {
        grid-template-columns:
            minmax(0, 760px);

        justify-content:
            center;
    }

    .testimonials__grid--double {
        grid-template-columns:
            repeat(2, minmax(0, 1fr));
    }

    .testimonials__grid--single
    .testimonial-card {
        min-height:
            300px;

        padding:
            40px;
    }

    .testimonials__grid--single
    .testimonial-card
    blockquote {
        font-size:
            clamp(
                20px,
                2.2vw,
                25px
            );
    }
}

@media (min-width: 1050px) {
    .testimonials {
        padding:
            130px 60px
            145px;
    }

    .testimonials__grid {
        grid-template-columns:
            repeat(3, 1fr);
    }

    .testimonials__grid--single {
        grid-template-columns:
            minmax(0, 820px);
    }

    .testimonials__grid--double {
        grid-template-columns:
            repeat(2, minmax(0, 1fr));

        max-width:
            900px;
    }
}
</style>